import classNames from 'classnames'
import {memo, useEffect, useLayoutEffect, useRef} from 'react'

import {useSyncedBooleanQueryParam} from '../../../../../hooks/routes'
import type {GetChangeLogApiArg} from '../../../../../services/rest'
import {restApi} from '../../../../../services/rest'
import {BS} from '../../../../../types/BS_types'
import {UserProperties} from '../../../../../utils/userProperties'

import styles from './ChangesGraph.css'

type Props = {
  className: string
  changesClassName: string
  height: number | undefined | null
  changeLogArg: GetChangeLogApiArg | null
}

function ChangesGraph({className, changesClassName, height, changeLogArg}: Props) {
  const initGraphRef = useRef(false)
  const [showGraph] = useSyncedBooleanQueryParam({
    param: 'showGraph',
    userProperty: UserProperties.SHOW_CHANGES_GRAPH,
    defaultTrue: true,
  })

  const [fetchChangeLog, {graph, hasGraph}] = restApi.endpoints.getChangeLog.useLazyQuery({
    selectFromResult: ({data}) => ({
      hasGraph: data?.graph != null,
      graph: data?.graph,
    }),
  })

  useEffect(() => {
    if (changeLogArg != null) {
      fetchChangeLog(changeLogArg, true)
    }
  }, [changeLogArg, fetchChangeLog])

  useLayoutEffect(() => {
    if (initGraphRef.current && BS?.ChangeLogGraph) {
      BS.ChangeLogGraph.restoreDefaultGraphView()
      BS.ChangeLogGraph.setGraphData(null)
      BS.ChangeLogGraph.redrawGraph()
      initGraphRef.current = false
    }
  }, [graph, showGraph])

  useLayoutEffect(() => {
    if (hasGraph && showGraph && BS?.ChangeLogGraph) {
      // A new object is passed to setGraphData, as it is modified in BS.ChangeLogGraph
      BS.ChangeLogGraph.setGraphData(JSON.parse(JSON.stringify(graph)))
      if (initGraphRef.current) {
        BS.ChangeLogGraph.redrawGraph()
      }
    }
  }, [graph, hasGraph, showGraph])

  useLayoutEffect(() => {
    if (hasGraph && showGraph && !initGraphRef.current && BS?.ChangeLogGraph) {
      BS.ChangeLogGraph.initGraph({
        graphContainerSelector: `.${styles.graph}`,
        changesTableSelector: `.${changesClassName}`,
        isSakuraUI: true,
      })
      initGraphRef.current = true
    }
  }, [graph, hasGraph, showGraph, className, changesClassName])

  useLayoutEffect(() => {
    if (initGraphRef.current && BS?.ChangeLogGraph) {
      BS.ChangeLogGraph.redrawGraph()
    }
  }, [height])

  return <div className={classNames(styles.graph, {[className]: hasGraph})} />
}

export default memo(ChangesGraph)
